import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Gift, KeyRound, Minus, Plus, QrCode, RefreshCw, Sparkles } from 'lucide-react';

import { queryKeys } from '@/lib/api/queryKeys';
import { eventsApi } from '@/features/events/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';
import { firstInvalidControl } from '@/lib/i18n/nativeValidation';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import { useAuthStore } from '@/lib/auth/authStore';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import { ApiError } from '@/lib/api/errors';
import { StationQrDialog } from '@/features/stations/components/StationQrDialog';
import { AllocationPrizeModeFields } from '@/features/stock/AllocationPrizeModeFields';
import type { StationInventoryItem } from '@/lib/api/types';

/**
 * Caso 11 — Booth-operator's single-station view. Mobile-first: big touch
 * targets, minimal chrome, just what someone running the table at an event
 * needs:
 *   - which survey is active
 *   - what stock is left per prize
 *   - shortcut to the canje flow (Caso 03) and the QR (existing page)
 *
 * 094 — y, si quien organiza lo activó en la estación, EDITAR: la
 * disponibilidad, el peso/cadencia y el «ojo» de cada regalo. Se desbloquea
 * con el código de seis dígitos de la estación, que devuelve un JWT de
 * operador con `edit: true`; hasta entonces la pantalla es sólo lectura, como
 * siempre. No se abren los juegos desde aquí: el operador ajusta el stand, no
 * juega por el invitado.
 */
export function OperatorStationPage() {
  // Event-day QR shows in place; no navigation away from the booth view.
  const [qrOpen, setQrOpen] = useState(false);
  const { id } = useParams<{ id: string }>();
  const stationId = Number(id);
  const navigate = useNavigate();
  const t = useT();
  const qc = useQueryClient();
  useFavoritable(t('operator.title'));
  // An operator session (Caso 11) is scoped to a single station — there is
  // no station list to go "back" to. Admins reaching this page from the
  // dashboard, by contrast, do want the back link.
  const isOperatorSession = useAuthStore((s) => s.app) === 'operator';
  const operatorEdit = useAuthStore((s) => !!(s.user as { operatorEdit?: boolean } | null)?.operatorEdit);
  const setSession = useAuthStore((s) => s.setSession);
  const sessionUser = useAuthStore((s) => s.user);

  const summary = useQuery({
    queryKey: queryKeys.stations.summary(stationId),
    queryFn: () => eventsApi.getStationSummary(stationId),
    enabled: Number.isFinite(stationId) && stationId > 0,
  });

  // ── Desbloqueo con el código ──────────────────────────────────────────
  const [codeOpen, setCodeOpen] = useState(false);
  const [code, setCode] = useState('');
  const unlock = useMutation({
    mutationFn: () => eventsApi.operatorUnlock(stationId, code.trim()),
    onSuccess: (r) => {
      if (sessionUser) {
        // El JWT elevado sustituye al de sólo lectura en esta misma sesión;
        // `setSession` vuelve a leer los claims (`operatorEdit` entre ellos).
        setSession({ token: r.access_token, user: { ...sessionUser, operatorEdit: true } as never, role: 'staff' });
      }
      setCodeOpen(false);
      setCode('');
      toast({ tone: 'success', title: t('operator.edit.unlocked') });
    },
    onError: (e: unknown) => {
      const status = e instanceof ApiError ? e.status : undefined;
      toast({
        tone: 'error',
        // 422 = código erróneo (el servidor no usa 401 aquí a propósito: el
        // interceptor lo leería como sesión caducada). 403 = la estación no
        // tiene la edición activada. 429 = demasiados intentos.
        title:
          status === 429
            ? t('operator.edit.tooMany')
            : status === 403
              ? t('operator.edit.notEnabled')
              : t('operator.edit.wrongCode'),
      });
    },
  });

  if (!Number.isFinite(stationId) || stationId <= 0) {
    return <div className="page">{t('operator.invalidId')}</div>;
  }

  if (summary.isLoading || !summary.data) {
    return (
      <div className="page space-y-4 max-w-md">
        <Skeleton className="h-10" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  const { station, event, survey, inventory } = summary.data;
  const totalRemaining = inventory.reduce((s, i) => s + (i.available ?? 0), 0);
  const lowStock = inventory.filter(
    (i) => typeof i.available === 'number' && i.available <= 3,
  );
  // Editar exige las dos cosas: que la estación lo permita y que ESTA sesión
  // haya desbloqueado con el código. Un admin desde el panel edita en la ficha
  // de la estación, no aquí.
  const canEdit = isOperatorSession && operatorEdit && station.operatorEditEnabled;
  const canUnlock = isOperatorSession && !operatorEdit && station.operatorEditEnabled;

  return (
    <div className="page space-y-5 max-w-md">
      {!isOperatorSession && (
        <button
          type="button"
          onClick={() => navigate('/stations')}
          className="inline-flex items-center gap-1 text-sm text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]"
        >
          <ArrowLeft className="h-4 w-4" /> {t('operator.back')}
        </button>
      )}

      <header>
        <Badge tone="accent">{event?.name ?? t('operator.unknownEvent')}</Badge>
        <h1 className="mt-2 text-3xl font-bold">{station.name}</h1>
        <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
          {survey?.name
            ? t('operator.surveyActive', { name: survey.name })
            : t('operator.noSurvey')}
        </p>
      </header>

      {/* Big primary actions for the booth operator. */}
      <div className="grid grid-cols-2 gap-3">
        <Button
          variant="primary"
          size="lg"
          // Caso 11 v2.5 — Hand-held operator flow goes straight to the
          // camera scanner; the `?scan=1` query param opens it on mount.
          onClick={() => navigate('/redeem?scan=1')}
          className="flex-col h-24 text-base"
        >
          <Gift className="mb-1 h-6 w-6" />
          {t('operator.cta.redeem')}
        </Button>
        <Button
          variant="secondary"
          size="lg"
          onClick={() => setQrOpen(true)}
          className="flex-col h-24 text-base"
        >
          <QrCode className="mb-1 h-6 w-6" />
          {t('operator.cta.showQr')}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2">
            <span>{t('operator.stock.title')}</span>
            <span className="flex items-center gap-2 text-sm font-normal text-[var(--color-fg-muted)]">
              {t('operator.stock.totalRemaining', { count: totalRemaining })}
              {/* Con el evento en marcha lo que queda cambia con cada premio. */}
              <button
                type="button"
                onClick={() => {
                  qc.invalidateQueries({ queryKey: queryKeys.stations.summary(stationId) });
                  toast({ tone: 'success', title: t('planner.station.refreshed') });
                }}
                aria-label={t('planner.station.refresh')}
                title={t('planner.station.refresh')}
                className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-[var(--color-bg-elevated)]"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {canUnlock ? (
            <Button
              variant="secondary"
              className="mb-3 w-full"
              leftIcon={<KeyRound className="h-4 w-4" />}
              onClick={() => setCodeOpen(true)}
            >
              {t('operator.edit.cta')}
            </Button>
          ) : null}
          {canEdit ? (
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-[var(--color-accent-ink)]">
              {t('operator.edit.editing')}
            </p>
          ) : null}
          {inventory.length === 0 ? (
            <p className="text-sm text-[var(--color-fg-muted)]">
              {t('operator.stock.empty')}
            </p>
          ) : (
            <ul className="divide-y divide-[var(--color-border-subtle)]">
              {inventory.map((row) => (
                <InventoryRow
                  key={row.id}
                  row={row}
                  stationId={stationId}
                  editable={canEdit}
                />
              ))}
            </ul>
          )}
          {lowStock.length > 0 && (
            <div className="mt-3 rounded-md bg-[var(--color-warn-soft,rgba(251,191,36,0.1))] p-2 text-xs">
              <Sparkles className="mr-1 inline h-3 w-3" />
              {t('operator.stock.lowAlert', { count: lowStock.length })}
            </div>
          )}
        </CardContent>
      </Card>

      <StationQrDialog
        open={qrOpen}
        onOpenChange={setQrOpen}
        stationId={Number(stationId)}
      />

      <Dialog open={codeOpen} onOpenChange={setCodeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('operator.edit.unlockTitle')}</DialogTitle>
            <DialogDescription>{t('operator.edit.unlockBody')}</DialogDescription>
          </DialogHeader>
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              const invalido = firstInvalidControl(e.currentTarget, t);
              if (invalido) {
                toast({ tone: 'error', title: invalido.message });
                return;
              }
              if (code.trim().length === 6) unlock.mutate();
            }}
          >
            <label className="block text-sm">
              <span className="mb-1 block text-[var(--color-fg-muted)]">{t('operator.edit.codeLabel')}</span>
              <Input
                autoFocus
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
                className="h-14 text-center font-mono text-2xl tracking-[0.4em]"
              />
            </label>
            <DialogFooter>
              <Button variant="ghost" type="button" onClick={() => setCodeOpen(false)}>
                {t('common.close')}
              </Button>
              <Button type="submit" loading={unlock.isPending} disabled={code.trim().length !== 6}>
                {t('operator.edit.unlock')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Una fila de regalo. En sólo lectura enseña lo que queda; desbloqueada,
 * deja tocar lo que queda (−/+), el peso o la cadencia, y el «ojo». Todo va
 * por el PATCH recortado del operador, que el servidor acota a esta estación
 * y a estos campos.
 */
function InventoryRow({
  row,
  stationId,
  editable,
}: {
  row: StationInventoryItem;
  stationId: number;
  editable: boolean;
}) {
  const t = useT();
  const qc = useQueryClient();
  const patch = useMutation({
    mutationFn: (body: Parameters<typeof eventsApi.operatorPatchAllocation>[2]) =>
      eventsApi.operatorPatchAllocation(stationId, row.id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.stations.summary(stationId) }),
    onError: (e: Error) =>
      toast({ tone: 'error', title: t('operator.edit.saveFailed'), description: e.message }),
  });
  const available = row.available ?? 0;
  const low = available <= 3;

  return (
    <li className={'py-2.5' + (row.hiddenFromGames ? ' opacity-60' : '')}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {row.stockImage ? (
            // `resolveImageUrl`: la ruta viene relativa al host del API
            // (`/uploads/…`); sin resolverla apuntaba al origen del frontend y
            // las fotos de los regalos no cargaban. Es el mismo helper que usa
            // el planner.
            <img
              src={resolveImageUrl(row.stockImage)}
              alt={row.stockName}
              className="h-10 w-10 shrink-0 rounded object-cover"
            />
          ) : (
            <div className="h-10 w-10 shrink-0 rounded bg-[var(--color-bg-elevated)]" />
          )}
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{row.stockName}</div>
            <div className="flex items-center gap-1 text-xs text-[var(--color-fg-subtle)]">
              {typeof row.rating === 'number' ? '★'.repeat(row.rating) : null}
              {row.hiddenFromGames ? <Badge tone="neutral">{t('planner.alloc.hidden')}</Badge> : null}
              {row.prizeMode === 'cooldown' ? (
                <Badge tone="neutral">{t('planner.alloc.mode.cooldown').replace('N', String(row.cooldownMinutes ?? ''))}</Badge>
              ) : null}
            </div>
          </div>
        </div>
        <div className={'flex items-center gap-1 text-right ' + (low ? 'text-[var(--color-warn)]' : '')}>
          {editable ? (
            <button
              type="button"
              disabled={patch.isPending || available <= 0}
              onClick={() => patch.mutate({ available: available - 1 })}
              aria-label={t('planner.alloc.decrease')}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--color-border-default)] disabled:opacity-30"
            >
              <Minus className="h-4 w-4" />
            </button>
          ) : null}
          <div className="min-w-[44px]">
            <div className="text-lg font-bold tabular-nums">{available}</div>
            <div className="text-[10px] uppercase text-[var(--color-fg-muted)]">
              {t('operator.stock.left')}
            </div>
          </div>
          {editable ? (
            <button
              type="button"
              disabled={patch.isPending || available >= row.quantity}
              onClick={() => patch.mutate({ available: available + 1 })}
              aria-label={t('planner.alloc.increase')}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--color-border-default)] disabled:opacity-30"
            >
              <Plus className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>
      {editable ? (
        <div className="mt-2 rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] p-2">
          <AllocationPrizeModeFields
            compact
            value={{
              prizeMode: row.prizeMode ?? 'weight',
              rating: row.rating ?? null,
              cooldownMinutes: row.cooldownMinutes ?? null,
              hiddenFromGames: !!row.hiddenFromGames,
            }}
            disabled={patch.isPending}
            onChange={(p) =>
              patch.mutate({
                ...(p.prizeMode ? { prizeMode: p.prizeMode } : {}),
                ...(typeof p.rating === 'number' ? { rating: p.rating } : {}),
                ...(typeof p.cooldownMinutes === 'number' ? { cooldownMinutes: p.cooldownMinutes } : {}),
                ...(typeof p.hiddenFromGames === 'boolean' ? { hiddenFromGames: p.hiddenFromGames } : {}),
              })
            }
          />
        </div>
      ) : null}
    </li>
  );
}
